import React from 'react';
import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import AnimatedNumber from './AnimatedNumber';
import { cn } from '@/lib/utils';

const ACCENTS = {
  primary: { text: 'text-primary', bg: 'bg-primary/10', stroke: 'hsl(var(--chart-1))' },
  violet: { text: 'text-tone-violet-text', bg: 'bg-tone-violet/10', stroke: 'hsl(var(--tone-violet))' },
  success: { text: 'text-success', bg: 'bg-success/10', stroke: 'hsl(var(--success))' },
  warning: { text: 'text-warning-text', bg: 'bg-warning/10', stroke: 'hsl(var(--warning))' },
  // Ink and sparkline stroke both move to `--info`. The stroke is a
  // meaningful graphic and `--chart-5` was 2.85:1 against the card.
  sky: { text: 'text-info-text', bg: 'bg-info/10', stroke: 'hsl(var(--info))' },
};

/**
 * KPI widget: animated number, optional sparkline, optional trend badge.
 * `trend` and `sparkline` are both optional -- when the caller has no real
 * historical data to back them, omit the prop rather than fabricate one.
 */
const MetricCard = ({
  label,
  value,
  format,
  icon: Icon,
  accent = 'primary',
  trend, // { direction: 'up'|'down'|'flat', value: number, label: string }
  sparkline, // number[]
  hint,
  loading = false,
  className,
}) => {
  const tone = ACCENTS[accent] || ACCENTS.primary;
  const sparkData = Array.isArray(sparkline) ? sparkline.map((v, i) => ({ i, v })) : null;

  if (loading) {
    return (
      <Card className={cn('p-5 space-y-3', className)}>
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-8 w-8 rounded-lg" />
        </div>
        <Skeleton className="h-8 w-28" />
        <Skeleton className="h-3 w-16" />
      </Card>
    );
  }

  return (
    <Card
      className={cn(
        'group relative overflow-hidden p-5 card-hover-lift animate-fade-in-up',
        className
      )}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
          {hint && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex h-3.5 w-3.5 cursor-help items-center justify-center rounded-full border border-input text-[9px] font-semibold text-muted-foreground">
                  i
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-[220px]">{hint}</TooltipContent>
            </Tooltip>
          )}
        </div>
        {Icon && (
          <div className={cn('flex h-8 w-8 items-center justify-center rounded-lg', tone.bg)}>
            <Icon className={cn('h-4 w-4', tone.text)} />
          </div>
        )}
      </div>

      <div className="mt-3 flex items-end justify-between gap-3">
        <div>
          <div className="text-2xl font-semibold tabular-nums tracking-tight text-foreground">
            <AnimatedNumber value={value} format={format} />
          </div>
          {trend && (
            <div
              className={cn(
                'mt-1.5 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-medium',
                trend.direction === 'up' && 'bg-success/10 text-success',
                trend.direction === 'down' && 'bg-destructive/10 text-destructive-text',
                trend.direction === 'flat' && 'bg-muted text-muted-foreground'
              )}
            >
              {trend.direction === 'up' && <ArrowUpRight className="h-3 w-3" />}
              {trend.direction === 'down' && <ArrowDownRight className="h-3 w-3" />}
              {trend.direction === 'flat' && <Minus className="h-3 w-3" />}
              {trend.value != null ? `${Math.abs(trend.value)}${trend.suffix ?? '%'}` : ''} {trend.label}
            </div>
          )}
        </div>

        {sparkData && sparkData.length > 1 && (
          <div className="h-10 w-20 shrink-0 opacity-80 transition-opacity group-hover:opacity-100">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={sparkData} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id={`spark-${label}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={tone.stroke} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={tone.stroke} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area
                  type="monotone"
                  dataKey="v"
                  stroke={tone.stroke}
                  strokeWidth={1.75}
                  fill={`url(#spark-${label})`}
                  isAnimationActive
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </Card>
  );
};

export default MetricCard;
